<template>
    <auth-box class="max-w-[480px]" :errors="props.errors">
        <form
            id="login-form"
            ref="formRef"
            method="POST"
            action="/login"
            class="flex flex-col gap-4"
            novalidate
            @submit.prevent="submit()"
        >
            <input name="remember" value="1" type="hidden" />
            <div>
                <vx-label> Email </vx-label>
                <vx-input
                    v-model="data.email"
                    type="email"
                    autofocus
                    name="email"
                    id="email"
                    size="lg"
                    data-test="email"
                    :error="v$.email.$error ? 'Email is invalid' : discoveryError"
                >
                </vx-input>
            </div>

            <div v-if="step === 'picker'" class="flex flex-col gap-2" data-test="team-picker">
                <div class="text-sm text-slate-600">Choose the account to log in to.</div>
                <vx-button
                    v-for="team in discoveredTeams"
                    :key="team.id"
                    :href="team.login_url"
                    color="primary"
                    size="lg"
                    data-test="team-login"
                >
                    Log In with SSO: {{ team.name }}
                    <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                </vx-button>
                <a href="#" class="block mx-auto text-slate-600 hover:text-slate-600 text-xs" data-test="use-password" @click.prevent="showPassword()">
                    Use a password instead
                </a>
            </div>

            <div v-if="step === 'password'">
                <vx-label> Password </vx-label>
                <vx-input
                    id="password"
                    ref="passwordRef"
                    type="password"
                    v-model="data.password"
                    data-test="password"
                    togglePassword
                    name="password"
                    size="lg"
                    :error="v$.password.$error ? 'Password is invalid' : ''"
                ></vx-input>
                <a
                    class="flex items-center mt-2 underline text-slate-600 hover:text-slate-600 text-xs"
                    :href="`/password/reset?${stringifyQueryString({ email: data.email })}`"
                >
                    <font-awesome-icon widthAuto :icon="faCircleInfo" class="mr-1"></font-awesome-icon>
                    Forgot Password?
                </a>
            </div>

            <div ref="recaptchaRef"></div>

            <div v-if="step !== 'picker'" class="flex flex-col gap-2 mt-4 md:flex-row-reverse">
                <vx-button
                    @click.prevent="submit()"
                    :loading="submitting"
                    data-test="submit"
                    class="md:grow"
                    type="submit"
                    color="primary"
                    size="lg"
                >
                    {{ step === 'password' ? 'Log In' : 'Continue' }}
                    <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                </vx-button>
                <vx-button v-if="step === 'password'" color="muted" size="lg" data-test="use-sso" @click.prevent="showEmail()">
                    Use single sign-on
                </vx-button>
            </div>

            <a
                v-if="step === 'email'"
                href="#"
                class="block mx-auto text-slate-600 hover:text-slate-600 text-xs"
                data-test="use-password"
                @click.prevent="showPassword()"
            >
                Have a password? Log in with it instead
            </a>

            <a
                v-if="step === 'password'"
                class="block mx-auto text-slate-600 hover:text-slate-600 text-xs"
                data-test="first-time-login"
                :href="`/password/reset?${stringifyQueryString({ email: data.email, first_time: 1 })}`"
            >
                First Time Login →
            </a>
        </form>
    </auth-box>
</template>
<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AuthBox from '../AuthBox.vue';
import { VxLabel, VxInput, VxButton } from '@voxie/frontend-components';
import { faArrowRight, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { ref, reactive, computed, nextTick, onMounted, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email, requiredIf } from '@vuelidate/validators';
import { validateRules } from '~/utils/validation';
import { stringifyQueryString } from '~/utils/string';
import { useRecaptcha } from '~/composables/useRecaptcha';
import { ssoClient } from '~/services';
import { HTTP_TOO_MANY_REQUESTS } from '~/support/HttpStatuses';

const props = defineProps({
    recaptchaKey: String,
    errors: Array,
    oldEmail: String,
});

const recaptcha = useRecaptcha(props.recaptchaKey ?? '');
const formRef = ref(null);
const passwordRef = ref(null);
const recaptchaRef = ref(null);

onMounted(() => {
    if (recaptchaRef.value) {
        recaptcha.render(recaptchaRef.value);
    }
});

const data = reactive({
    email: props.oldEmail || '',
    password: '',
});

const step = ref(props.oldEmail ? 'password' : 'email');
const submitting = ref(false);
const discoveryError = ref('');
const discoveredTeams = ref([]);

const v$ = useVuelidate(
    {
        email: { required, email },
        password: { required: requiredIf(computed(() => step.value === 'password')) },
    },
    data
);

const showPassword = async () => {
    step.value = 'password';
    discoveryError.value = '';
    await nextTick();
    passwordRef.value?.field?.focus();
};

const showEmail = () => {
    step.value = 'email';
    data.password = '';
    v$.value.$reset();
};

watch(
    () => data.email,
    () => {
        if (step.value === 'picker') {
            step.value = 'email';
            discoveredTeams.value = [];
        }
    }
);

const discover = async (recaptchaToken) => {
    const email = data.email;
    // The person may pick a password or retype the email while the answer is out; a stale answer must not act.
    const stale = () => step.value === 'password' || data.email !== email;

    try {
        const { data: response } = await ssoClient.discover(email, recaptchaToken);

        if (stale()) {
            return;
        }

        if (response.teams.length === 1) {
            window.location.assign(response.teams[0].login_url);
            return;
        }

        if (response.teams.length > 1) {
            discoveredTeams.value = response.teams;
            step.value = 'picker';
            return;
        }

        await showPassword();
    } catch (error) {
        if (stale()) {
            return;
        }

        if (error?.response?.status === HTTP_TOO_MANY_REQUESTS) {
            discoveryError.value = 'Too many attempts. Wait a minute and try again.';
            return;
        }

        await showPassword();
    } finally {
        recaptcha.reset();
        submitting.value = false;
    }
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    discoveryError.value = '';

    const isFormCorrect = await validateRules(v$);
    if (!isFormCorrect) {
        submitting.value = false;
        return;
    }

    let recaptchaToken;
    try {
        recaptchaToken = await recaptcha.execute();
    } catch {
        submitting.value = false;
        return;
    }

    if (step.value !== 'password') {
        await discover(recaptchaToken);
        return;
    }

    formRef.value?.submit();
};

Object.keys(window.sessionStorage)
    .filter((key) => /displayedNotActiveTeam/.test(key))
    .forEach((key) => window.sessionStorage.removeItem(key));
</script>
